// Task Execution View (Phase 3 Enhanced)
// Step-by-step task execution with evidence capture, validation, deviations, and compliance

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Clock, CheckCircle, XCircle, AlertTriangle, Play, SkipForward, Camera, AlertCircle, MapPin, Pause, PlayCircle } from 'lucide-react';
import { useTaskMutations } from '../../hooks/useTasks';
import { taskService } from '../../services/taskService';
import { taskExecutionService } from '../../services/taskExecutionService';
import { evidenceService } from '../../services/evidenceService';
import { executionValidationService } from '../../services/executionValidationService';
import { deviationService } from '../../services/deviationService';
import { complianceService } from '../../services/complianceService';
import { riskAssessmentEngine } from '../../services/riskAssessmentEngine';
import { getRiskLevelConfig } from '../../types/risk';
import RiskAssessmentDashboard from '../risks/RiskAssessmentDashboard';
import EvidenceCapture from '../execution/EvidenceCapture';
import DeviationAlert from '../execution/DeviationAlert';
import ComplianceScoreDisplay from '../execution/ComplianceScoreDisplay';
import Modal from '../ui/Modal';
import authService from '../../services/authService';
import './TaskExecutionView.css';

const TaskExecutionView = ({ task, userId, onBack }) => {
  const { t } = useTranslation(['tasks', 'common']);
  const [currentProtocolIndex, setCurrentProtocolIndex] = useState(task.currentProtocolIndex || 0);
  const [currentStepIndex, setCurrentStepIndex] = useState(task.currentStepIndex || 0);
  const [stepNotes, setStepNotes] = useState('');
  const [skipReason, setSkipReason] = useState('');
  const [showSkipDialog, setShowSkipDialog] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [taskData, setTaskData] = useState(task);
  const [riskAssessment, setRiskAssessment] = useState(null);
  const [showRiskAssessment, setShowRiskAssessment] = useState(false);
  
  // Phase 3: Execution tracking
  const [executionId, setExecutionId] = useState(null);
  const [execution, setExecution] = useState(null);
  const [executorInfo, setExecutorInfo] = useState({ name: '', role: '' });
  
  // Phase 3: Evidence capture
  const [showEvidenceCapture, setShowEvidenceCapture] = useState(false);
  const [capturedEvidence, setCapturedEvidence] = useState([]);
  
  // Phase 3: Validation and deviations
  const [currentDeviation, setCurrentDeviation] = useState(null);
  const [showDeviationAlert, setShowDeviationAlert] = useState(false);
  
  // Phase 3: Compliance
  const [complianceData, setComplianceData] = useState(null);
  const [showComplianceScore, setShowComplianceScore] = useState(false);
  const [complianceModalContext, setComplianceModalContext] = useState(null); // 'protocol' | 'task' for close message
  
  // Phase 3: Pause/resume
  const [isPaused, setIsPaused] = useState(false);
  
  // Phase 3: Modals for browser dialog replacements
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: '' });
  const [successModal, setSuccessModal] = useState({ isOpen: false, message: '' });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, message: '', onConfirm: null });

  const {
    startProtocol,
    completeStep,
    skipStep,
    completeProtocol,
    completeTask,
    setStepActionCompleted,
    loading
  } = useTaskMutations();

  const currentProtocol = taskData.protocols[currentProtocolIndex];
  const steps = currentProtocol?.steps ?? [];
  // If the step at currentStepIndex is already complete (e.g. all actions checked), show first incomplete step so user keeps control
  const effectiveStepIndex = (() => {
    for (let i = currentStepIndex; i < steps.length; i++) {
      const s = steps[i];
      if (!s?.isComplete && !s?.isSkipped) return i;
    }
    return currentStepIndex;
  })();
  const currentStep = steps[effectiveStepIndex];

  // When switching to a different task, sync from prop so we show correct step
  useEffect(() => {
    if (task?.id && task.id !== taskData?.id) {
      setTaskData(task);
      setCurrentProtocolIndex(task.currentProtocolIndex ?? 0);
      setCurrentStepIndex(task.currentStepIndex ?? 0);
    }
  }, [task?.id]);

  // Sync isPaused from execution status (e.g. on reload)
  useEffect(() => {
    if (execution?.status === 'paused') setIsPaused(true);
    else if (execution?.status === 'in_progress') setIsPaused(false);
  }, [execution?.status]);

  // Phase 3: Load executor info and create execution record
  useEffect(() => {
    const loadExecutorInfo = async () => {
      try {
        const userProfile = await authService.getCurrentUserProfile();
        if (userProfile) {
          setExecutorInfo({
            name: userProfile.full_name || userProfile.name || 'Unknown',
            role: userProfile.role || 'executor'
          });
        }
      } catch (error) {
        console.error('Error loading executor info:', error);
      }
    };
    
    loadExecutorInfo();
    
    // Phase 3: Create execution record if task is in progress
    const createExecutionRecord = async () => {
      if (taskData.status === 'in_progress' && !executionId && userId) {
        try {
          const execId = await taskExecutionService.createExecution(
            taskData.id,
            taskData.protocols[0]?.protocolId || '',
            userId,
            executorInfo.name || 'Unknown',
            executorInfo.role || 'executor',
            userId
          );
          setExecutionId(execId);
          
          // Load execution data
          const exec = await taskExecutionService.getExecution(execId);
          setExecution(exec);
        } catch (error) {
          console.error('Error creating execution record:', error);
        }
      } else if (taskData.status === 'in_progress' && !executionId) {
        // Try to find existing execution
        try {
          const exec = await taskExecutionService.getExecutionByTaskId(taskData.id);
          if (exec) {
            setExecutionId(exec.id);
            setExecution(exec);
          }
        } catch (error) {
          console.error('Error loading execution:', error);
        }
      }
    };
    
    if (executorInfo.name) {
      createExecutionRecord();
    }
  }, [taskData.status, userId, executorInfo.name]);
  
  // Calculate risk assessment when task starts
  useEffect(() => {
    const calculateTaskRisk = async () => {
      try {
        const assessment = await riskAssessmentEngine.calculateTaskRisk(taskData, taskData.context);
        setRiskAssessment(assessment);
        console.log('✅ Task risk assessment calculated:', assessment);
      } catch (error) {
        console.error('Error calculating task risk:', error);
      }
    };
    
    if (taskData) {
      calculateTaskRisk();
    }
  }, [taskData.id]); // Only calculate once when task loads
  
  // Timer for current step
  useEffect(() => {
    if (currentStep && currentStep.status === 'in_progress' && currentStep.startTime) {
      const interval = setInterval(() => {
        const now = Date.now();
        const start = currentStep.startTime.toMillis ? currentStep.startTime.toMillis() : currentStep.startTime.getTime();
        setElapsedTime(Math.floor((now - start) / 1000));
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [currentStep]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartProtocol = async () => {
    try {
      // Phase 3: Create execution record if not exists
      if (!executionId) {
        const execId = await taskExecutionService.createExecution(
          taskData.id,
          currentProtocol.protocolId || '',
          userId,
          executorInfo.name || 'Unknown',
          executorInfo.role || 'executor',
          userId
        );
        setExecutionId(execId);
        
        const exec = await taskExecutionService.getExecution(execId);
        setExecution(exec);
      }
      
      await startProtocol(taskData.id, currentProtocolIndex, userId);
      
      // Phase 3: Update execution status
      if (executionId) {
        await taskExecutionService.updateStatus(executionId, 'in_progress', userId);
      }
      
      // Refresh task data
      const updatedTask = await taskService.getTask(taskData.id);
      setTaskData(updatedTask);
    } catch (error) {
      setErrorModal({ isOpen: true, message: `${t('errorStartingProtocol')} ${error.message}` });
    }
  };

  const handleCompleteStep = async () => {
    try {
      if (!currentStep) return;
      
      // Phase 3: Collect evidence for this step
      const stepEvidence = capturedEvidence.filter(e => 
        e.step_id === currentStep.stepId || e.step_id === currentStep.stepId
      );
      
      // Phase 3: Validate action if execution record exists
      let validationResult = null;
      if (execution && executionId) {
        try {
          validationResult = await executionValidationService.validateAction(execution, {
            action_id: currentStep.stepId,
            step_id: currentStep.stepId,
            step_number: effectiveStepIndex + 1,
            duration_seconds: elapsedTime,
            expected_duration_seconds: currentStep.expectedDuration,
            photo_urls: stepEvidence.filter(e => e.evidence_type === 'photo').map(e => e.photo_url),
            signature_url: stepEvidence.find(e => e.evidence_type === 'signature')?.signature_url,
            notes: stepNotes,
            validation_passed: true
          });
          
          // Phase 3: Detect deviations if validation fails or warnings (PRD-compliant with context)
          if (validationResult && (!validationResult.passed || validationResult.warnings.length > 0)) {
            const ctx = taskData?.context || {};
            const deviations = await deviationService.detectDeviations(execution, {
              step_id: currentStep.stepId,
              step_number: effectiveStepIndex + 1,
              duration_seconds: elapsedTime,
              expected_duration_seconds: currentStep.expectedDuration,
              validation_passed: validationResult.passed
            }, {
              organizationId: taskData?.organizationId ?? ctx.organizationId,
              taskInstanceId: taskData?.id,
              protocolId: currentProtocol?.protocolId || currentProtocol?.id,
              protocolName: currentProtocol?.protocolName || currentProtocol?.name,
              departmentId: ctx.departmentId,
              roomId: ctx.roomId,
              locationPath: [ctx.departmentName, ctx.roomName].filter(Boolean).join(' / ') || null,
              executorId: userId,
              executorName: executorInfo?.name || taskData?.assignedToName,
              riskId: taskData?.risk_data?.critical_risks?.[0]?.id,
              riskName: taskData?.risk_data?.critical_risks?.[0]?.scenario,
              biologicalEntities: taskData?.risk_data?.biological_entities?.map((e) => e.name) || []
            });
            
            if (deviations.length > 0) {
              setCurrentDeviation(deviations[0]);
              setShowDeviationAlert(true);
              return; // Wait for user to acknowledge deviation
            }
          }
        } catch (validationError) {
          console.error('Validation error:', validationError);
          // Continue with completion even if validation fails
        }
      }
      
      // Phase 3: Update execution step record
      if (executionId) {
        await taskExecutionService.updateStep(executionId, {
          step_id: currentStep.stepId,
          step_number: effectiveStepIndex + 1,
          started_at: currentStep.startTime || new Date(),
          completed_at: new Date(),
          duration_seconds: elapsedTime,
          expected_duration_seconds: currentStep.expectedDuration,
          actions_completed: (currentStep.actions || []).map((action, idx) => ({
            action_id: action.actionId || action.id,
            action_number: idx + 1,
            started_at: new Date(),
            completed_at: new Date(),
            duration_seconds: 0,
            validation_passed: action.completed === true,
            expected_duration_seconds: action.expectedDuration ?? currentStep.expectedDuration,
            photo_urls: stepEvidence.filter(e => e.evidence_type === 'photo').map(e => e.photo_url),
            signature_url: stepEvidence.find(e => e.evidence_type === 'signature')?.signature_url,
            notes: stepNotes
          })),
          validation_passed: validationResult?.passed !== false,
          deviation_detected: validationResult?.warnings?.length > 0
        }, userId);
      }
      
      await completeStep(taskData.id, currentProtocolIndex, effectiveStepIndex, userId, {
        notes: stepNotes,
        photos: stepEvidence.filter(e => e.evidence_type === 'photo').map(e => e.photo_url),
        documentation: null
      });

      // Clear step data
      setStepNotes('');
      setCapturedEvidence(prev => prev.filter(e => 
        !(e.step_id === currentStep.stepId || e.step_id === currentStep.stepId)
      ));
      
      // Refresh task data
      const updatedTask = await taskService.getTask(taskData.id);
      setTaskData(updatedTask);
      
      // Refresh execution
      if (executionId) {
        const exec = await taskExecutionService.getExecution(executionId);
        setExecution(exec);
      }
      
      // Advance to next step or protocol
      if (effectiveStepIndex < currentProtocol.steps.length - 1) {
        setCurrentStepIndex(effectiveStepIndex + 1);
      } else {
        if (currentProtocolIndex < taskData.protocols.length - 1) {
          setCurrentProtocolIndex(currentProtocolIndex + 1);
          setCurrentStepIndex(0);
        }
      }
    } catch (error) {
      setErrorModal({ isOpen: true, message: `${t('errorCompletingStep')} ${error.message}` });
    }
  };

  const handleSkipStep = async () => {
    if (!skipReason.trim()) {
      setErrorModal({ isOpen: true, message: 'Please provide a reason for skipping this step' });
      return;
    }

    try {
      // Phase 3: Record deviation for skipped step (PRD-compliant with context)
      if (executionId && currentStep) {
        const ctx = taskData?.context || {};
        await deviationService.createDeviation(executionId, {
          deviation_type: 'sequence',
          deviation_category: 'sequence',
          severity: 'minor',
          description: `Step skipped: ${currentStep.stepName}. Reason: ${skipReason}`,
          step_number: effectiveStepIndex + 1,
          detected_by: 'executor',
          detection_method: 'manual_skip'
        }, {
          organizationId: taskData?.organizationId ?? ctx.organizationId,
          taskInstanceId: taskData?.id,
          protocolId: currentProtocol?.protocolId || currentProtocol?.id,
          protocolName: currentProtocol?.protocolName || currentProtocol?.name,
          departmentId: ctx.departmentId,
          roomId: ctx.roomId,
          executorId: userId,
          executorName: executorInfo?.name || taskData?.assignedToName
        });
      }
      
      await skipStep(taskData.id, currentProtocolIndex, effectiveStepIndex, userId, skipReason);

      // Move to next step
      setSkipReason('');
      setShowSkipDialog(false);
      
      // Refresh task data
      const updatedTask = await taskService.getTask(taskData.id);
      setTaskData(updatedTask);
      
      // Refresh execution
      if (executionId) {
        const exec = await taskExecutionService.getExecution(executionId);
        setExecution(exec);
      }
      
      if (effectiveStepIndex < currentProtocol.steps.length - 1) {
        setCurrentStepIndex(effectiveStepIndex + 1);
      } else {
        if (currentProtocolIndex < taskData.protocols.length - 1) {
          setCurrentProtocolIndex(currentProtocolIndex + 1);
          setCurrentStepIndex(0);
        }
      }
    } catch (error) {
      setErrorModal({ isOpen: true, message: `${t('errorSkippingStep')} ${error.message}` });
    }
  };

  const handleCompleteProtocol = async () => {
    try {
      await completeProtocol(taskData.id, currentProtocolIndex, userId);
      const updated = await taskService.getTask(taskData.id);
      setTaskData(updated);
      if (currentProtocolIndex < (taskData.protocols?.length ?? 0) - 1) {
        setCurrentProtocolIndex(currentProtocolIndex + 1);
        setCurrentStepIndex(0);
      }
      setShowRiskAssessment(true);
      // Show compliance report popup - prefer task-based (source of truth)
      let protocolCompliance = complianceService.calculateFromTask(updated);
      if (!protocolCompliance && execution) {
        try {
          protocolCompliance = await complianceService.calculateComplianceScore(execution);
        } catch (e) { /* ignore */ }
      }
      if (protocolCompliance) {
        setComplianceData(protocolCompliance);
        setComplianceModalContext('protocol');
        setShowComplianceScore(true);
      } else {
        setSuccessModal({ isOpen: true, message: (updated.protocols?.every(p => p.status === 'completed' || p.status === 'completed_with_skips' || p.status === 'skipped') ? t('protocolCompletedReady') : t('protocolCompletedContinue')) });
      }
    } catch (error) {
      setErrorModal({ isOpen: true, message: error?.message || 'Failed to complete protocol' });
    }
  };

  const handleCompleteTask = async () => {
    setConfirmModal({
      isOpen: true,
      message: t('confirmCompleteTask'),
      onConfirm: async () => {
        setConfirmModal({ isOpen: false, message: '', onConfirm: null });
        
        try {
          // Phase 3: Calculate compliance score
          // Prefer task-based (source of truth) - reflects actual step/action completion
          let complianceData = complianceService.calculateFromTask(taskData);
          if (!complianceData && execution) {
            try {
              complianceData = await complianceService.calculateComplianceScore(execution);
            } catch (complianceError) {
              console.error('Error calculating compliance:', complianceError);
            }
          }
          if (complianceData && executionId) {
            await taskExecutionService.completeExecution(executionId, {
              completion_notes: '',
              verification_status: 'pending',
              compliance_score: complianceData.overall_score,
              compliance_breakdown: complianceData.breakdown
            }, userId);
          }
          
          await completeTask(taskData.id, userId, {
            completionQuality: complianceData?.grade?.grade === 'A+' ? 'excellent' : 
                               complianceData?.grade?.grade?.startsWith('A') ? 'good' :
                               complianceData?.grade?.grade?.startsWith('B') ? 'acceptable' : 'poor',
            notes: ''
          });

          // Phase 2.1: Threshold evaluation runs in taskService.completeTask (centralized)
          
          // Phase 3: Show compliance score
          if (complianceData) {
            setComplianceData(complianceData);
            setComplianceModalContext('task');
            setShowComplianceScore(true);
          } else {
            setSuccessModal({ isOpen: true, message: 'Task completed successfully!' });
          }
        } catch (error) {
          setErrorModal({ isOpen: true, message: `${t('errorCompletingTask')} ${error.message}` });
        }
      }
    });
  };
  
  const handleContinueAfterDeviation = async (notes) => {
    setShowDeviationAlert(false);
    // Continue with step completion
    await handleCompleteStep();
  };
  
  const handleEvidenceCaptured = (evidence) => {
    setCapturedEvidence(prev => [...prev, evidence]);
    setShowEvidenceCapture(false);
  };

  const handlePause = async () => {
    if (!executionId || !userId) return;
    try {
      await taskExecutionService.updateStatus(executionId, 'paused', userId);
      setIsPaused(true);
      const exec = await taskExecutionService.getExecution(executionId);
      setExecution(exec);
    } catch (err) {
      setErrorModal({ isOpen: true, message: err?.message || 'Failed to pause' });
    }
  };

  const handleResume = async () => {
    if (!executionId || !userId) return;
    try {
      await taskExecutionService.updateStatus(executionId, 'in_progress', userId);
      setIsPaused(false);
      const exec = await taskExecutionService.getExecution(executionId);
      setExecution(exec);
    } catch (err) {
      setErrorModal({ isOpen: true, message: err?.message || 'Failed to resume' });
    }
  };
  
  const handleCloseComplianceScore = () => {
    setShowComplianceScore(false);
    const message = complianceModalContext === 'protocol'
      ? 'Protocol completed. Continue with the next protocol or complete the task.'
      : 'Task completed successfully!';
    setComplianceModalContext(null);
    setSuccessModal({ isOpen: true, message });
  };

  const getOverallProgress = () => {
    let totalSteps = 0;
    let completedSteps = 0;
    
    taskData.protocols.forEach(protocol => {
      totalSteps += protocol.steps.length;
      completedSteps += protocol.steps.filter(s => s.isComplete || s.isSkipped).length;
    });
    
    if (totalSteps === 0) return 0;
    return Math.round((completedSteps / totalSteps) * 100);
  };

  const allProtocolsComplete = taskData.protocols.every(p =>
    p.status === 'completed' || p.status === 'completed_with_skips' || p.status === 'skipped'
  );

  // When "Complete Protocol" section is visible (all steps done, protocol not completed), auto-expand risk assessment
  const protocolStepsAllDone = currentProtocol?.steps?.every(s => s.isComplete || s.isSkipped) && currentProtocol?.status !== 'completed' && currentProtocol?.status !== 'completed_with_skips';
  useEffect(() => {
    if (protocolStepsAllDone && riskAssessment) {
      setShowRiskAssessment(true);
    }
  }, [protocolStepsAllDone, riskAssessment]);

  return (
    <div className="task-execution-view">
      {/* Header */}
      <div className="execution-header">
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={onBack}>
            <ArrowLeft size={20} /> {t('backToTasks')}
          </button>
          {executionId && !allProtocolsComplete && (
            isPaused ? (
              <button className="btn btn-primary" onClick={handleResume}>
                <PlayCircle size={20} /> {t('tasks:resume') || 'Resume'}
              </button>
            ) : (
              <button className="btn btn-secondary" onClick={handlePause}>
                <Pause size={20} /> {t('tasks:pause') || 'Pause'}
              </button>
            )
          )}
        </div>
        <div className="task-info">
          <h2>{taskData.name}</h2>
          <div className="task-meta">
            <span className={`priority-badge priority-${taskData.priority}`}>
              {taskData.priority === 'high' ? t('priorityHigh') : taskData.priority === 'medium' ? t('priorityMedium') : taskData.priority === 'low' ? t('priorityLow') : taskData.priority}
            </span>
            <span className="meta-item">
              {taskData.protocols.length} {t('protocolsCountShort')}
            </span>
            <span className="meta-item">
              {t('progress')}: {getOverallProgress()}%
            </span>
            {(taskData.context?.roomName || taskData.context?.departmentName) && (
              <span className="meta-item task-execute-at">
                <MapPin size={14} />
                {[taskData.context.departmentName, taskData.context.roomName].filter(Boolean).join(' → ')}
              </span>
            )}
            {taskData.resolvedRiskDrivenSchedule && (
              <span className="meta-item task-risk-driven-schedule" title={taskData.resolvedRiskDrivenSchedule.inherited ? t('tasks:riskLevelInheritedFromParent') : undefined}>
                <Clock size={14} />
                {taskData.resolvedRiskDrivenSchedule.frequencyLabel} — {t('tasks:basedOnRiskClassificationOf', { level: taskData.resolvedRiskDrivenSchedule.criticalityLevelLabel, location: taskData.resolvedRiskDrivenSchedule.sourceLocationName })}
                {taskData.resolvedRiskDrivenSchedule.inherited && ` (${t('tasks:inherited')})`}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Risk Assessment Indicator */}
      {riskAssessment && (
        <div className="task-risk-indicator">
          <div className="risk-indicator-compact">
            <div 
              className="risk-level-badge"
              style={{ 
                backgroundColor: getRiskLevelConfig(riskAssessment.riskLevel).color 
              }}
            >
              {getRiskLevelConfig(riskAssessment.riskLevel).icon} {riskAssessment.riskLevel}
            </div>
            <div className="risk-score-display">
              {t('riskScore')}: <strong>{riskAssessment.finalScore}</strong>
            </div>
            <button 
              className="view-assessment-btn"
              onClick={() => setShowRiskAssessment(!showRiskAssessment)}
            >
              {showRiskAssessment ? t('hideRiskAssessment') : t('viewRiskAssessment')}
            </button>
          </div>
          
          {showRiskAssessment && riskAssessment.assessments && riskAssessment.assessments.length > 0 && (
            <div className="risk-assessment-detail">
              <RiskAssessmentDashboard 
                baseRisk={riskAssessment.assessments[0]}
                context={taskData.context}
                showRecommendations={true}
              />
            </div>
          )}
        </div>
      )}

      {/* Paused overlay */}
      {isPaused && (
        <div className="execution-paused-banner">
          <Pause size={24} />
          <span>{t('tasks:executionPaused') || 'Execution paused'}</span>
          <button className="btn btn-primary" onClick={handleResume}>
            <PlayCircle size={20} /> {t('tasks:resume') || 'Resume'}
          </button>
        </div>
      )}

      {/* Overall Progress */}
      <div className="overall-progress">
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${getOverallProgress()}%` }}></div>
        </div>
      </div>

      {/* Protocol tabs – only when multiple protocols */}
      {taskData.protocols.length > 1 && (
        <div className="protocol-tabs">
          {taskData.protocols.map((protocol, index) => (
            <button
              key={index}
              className={`protocol-tab ${index === currentProtocolIndex ? 'active' : ''} ${(protocol.status === 'completed' || protocol.status === 'completed_with_skips') ? 'completed' : ''}`}
              onClick={() => {
                setCurrentProtocolIndex(index);
                setCurrentStepIndex(0);
              }}
            >
              <span className="protocol-number">{index + 1}</span>
              <span className="protocol-name">{protocol.protocolName}</span>
              {(protocol.status === 'completed' || protocol.status === 'completed_with_skips') && <CheckCircle size={16} />}
              {protocol.status === 'completed_with_skips' && <span className="protocol-tab-with-skips"> {t('withSkipsErrors')}</span>}
            </button>
          ))}
        </div>
      )}


      {/* Current Protocol Execution */}
      {currentProtocol && (
        <div className="protocol-execution">
          <div className="protocol-header">
            <h3>
              {taskData.protocols.length > 1
                ? t('protocolNumber', { number: currentProtocolIndex + 1, name: currentProtocol.protocolName })
                : currentProtocol.protocolName}
            </h3>
            <span className={`status-badge status-${currentProtocol.status}`}>
              {currentProtocol.status}
            </span>
          </div>

          {/* Steps List */}
          <div className="steps-list">
            {currentProtocol.steps.map((step, index) => (
              <div
                key={index}
                className={`step-item ${index === effectiveStepIndex ? 'current' : ''} ${step.isComplete ? 'completed' : ''} ${step.isSkipped ? 'skipped' : ''} ${step.completedWithErrors ? 'completed-with-errors' : ''}`}
              >
                <div className="step-indicator">
                  {step.isComplete ? (
                    step.completedWithErrors ? (
                      <AlertTriangle size={20} color="#d97706" />
                    ) : (
                      <CheckCircle size={20} color="#10b981" />
                    )
                  ) : step.isSkipped ? (
                    <XCircle size={20} color="#f59e0b" />
                  ) : index === effectiveStepIndex ? (
                    <div className="current-indicator"></div>
                  ) : (
                    <div className="pending-indicator">{index + 1}</div>
                  )}
                </div>

                <div className="step-content">
                  <h4>{step.stepName}{step.completedWithErrors && <span className="step-label-with-errors"> (completed with errors)</span>}</h4>
                  {step.isSkipped && step.skipReason && (
                    <p className="skip-reason">Skipped: {step.skipReason}</p>
                  )}
                  {step.notes && (
                    <p className="step-notes">Notes: {step.notes}</p>
                  )}
                </div>

                <div className="step-timing">
                  {step.duration ? (
                    <span className={step.isOnTime ? 'on-time' : 'late'}>
                      {formatTime(step.duration)} / {formatTime(step.expectedDuration)}
                    </span>
                  ) : (
                    <span className="expected-time">
                      ~{formatTime(step.expectedDuration)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Current Step Execution - show whenever there is a current step (including when complete, so Complete Step + risk are always visible) */}
          {currentStep && (
            <div className="current-step-execution">
              <div className="step-execution-header">
                <h4>{t('currentStepLabel')}: {currentStep.stepName}</h4>
                {(currentStep.isComplete || currentStep.isSkipped) ? (
                  <span className={`step-status-badge step-done ${currentStep.completedWithErrors ? 'step-with-errors' : ''}`}>
                    <CheckCircle size={16} /> {currentStep.isSkipped ? t('skipped') : (currentStep.completedWithErrors ? t('completeWithErrors') : t('completed'))}
                  </span>
                ) : (
                  currentStep.status === 'in_progress' && currentStep.expectedDuration > 0 && (
                    <div className="timer">
                      <Clock size={16} />
                      <span className={elapsedTime > currentStep.expectedDuration ? 'overtime' : ''}>
                        {formatTime(elapsedTime)} / {formatTime(currentStep.expectedDuration)}
                      </span>
                      {elapsedTime < currentStep.expectedDuration && (
                        <span className="countdown">
                          {formatTime(currentStep.expectedDuration - elapsedTime)} {t('tasks:remaining') || 'remaining'}
                        </span>
                      )}
                    </div>
                  )
                )}
              </div>

              {/* When step already complete/skipped: show summary and Complete Step to advance */}
              {(currentStep.isComplete || currentStep.isSkipped) ? (
                <div className="step-complete-summary">
                  <p>
                    {currentStep.isSkipped ? 'This step was skipped.' : currentStep.completedWithErrors ? 'This step was completed with errors (not all actions were done).' : 'This step is done.'}
                    {' '}Click <strong>Complete Step</strong> to continue to the next step or complete the protocol.
                  </p>
                  <div className="step-actions">
                    <button
                      className="btn btn-success"
                      onClick={handleCompleteStep}
                      disabled={loading || isPaused}
                    >
                      <CheckCircle size={16} /> Complete Step
                    </button>
                  </div>
                </div>
              ) : (
                <>
              {/* Actions Checklist with protocol action details – click action or checkbox to mark complete */}
              {currentStep.actions && currentStep.actions.length > 0 && (
                <div className="actions-checklist">
                  <h5>{t('actionsLabel')}:</h5>
                  {currentStep.actions.map((action, index) => {
                    const handleToggleAction = async () => {
                      if (isPaused) return;
                      try {
                        const newCompleted = !action.completed;
                        await setStepActionCompleted(
                          taskData.id,
                          currentProtocolIndex,
                          effectiveStepIndex,
                          index,
                          newCompleted,
                          userId
                        );
                        const updated = await taskService.getTask(taskData.id);
                        setTaskData(updated);
                        // If current step is now complete (all actions checked), advance so next step is shown
                        const proto = updated?.protocols?.[currentProtocolIndex];
                        const stepNow = proto?.steps?.[effectiveStepIndex];
                        if (stepNow?.isComplete && effectiveStepIndex < (proto?.steps?.length ?? 0) - 1) {
                          setCurrentStepIndex(effectiveStepIndex + 1);
                        } else if (stepNow?.isComplete && effectiveStepIndex === (proto?.steps?.length ?? 0) - 1 && currentProtocolIndex < (updated?.protocols?.length ?? 0) - 1) {
                          setCurrentProtocolIndex(currentProtocolIndex + 1);
                          setCurrentStepIndex(0);
                        }
                      } catch (err) {
                        setErrorModal({ isOpen: true, message: err.message || 'Failed to update action' });
                      }
                    };
                    return (
                    <div
                      key={action.actionId || index}
                      className={`action-item action-item-detailed ${action.completed ? 'action-completed' : ''}`}
                      onClick={handleToggleAction}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggleAction(); } }}
                      role="button"
                      tabIndex={0}
                      aria-pressed={action.completed}
                      aria-label={`${action.actionName}, ${action.completed ? 'completed' : 'not completed'}. Click to toggle.`}
                    >
                      <div className="action-item-header">
                        <input
                          type="checkbox"
                          checked={action.completed}
                          readOnly
                          tabIndex={-1}
                          aria-hidden
                        />
                        <span className="action-name">{action.actionName}</span>
                        {action.estimatedDuration != null && action.estimatedDuration > 0 && (
                          <span className="action-duration">~{action.estimatedDuration} min</span>
                        )}
                      </div>
                      {action.description && (
                        <p className="action-description">{action.description}</p>
                      )}
                      {(action.equipment && action.equipment.length > 0) && (
                        <div className="action-detail-row">
                          <strong>{t('equipmentLabel')}:</strong>{' '}
                          {action.equipment.map((eq, i) => (typeof eq === 'string' ? eq : eq?.name)).filter(Boolean).join(', ')}
                        </div>
                      )}
                      {(action.supplies && action.supplies.length > 0) && (
                        <div className="action-detail-row">
                          <strong>Supplies:</strong>{' '}
                          {action.supplies.map((s, i) => (typeof s === 'string' ? s : s?.name)).filter(Boolean).join(', ')}
                        </div>
                      )}
                      {action.verificationMethod && (
                        <div className="action-detail-row">
                          <strong>{t('verificationLabel')}:</strong> {action.verificationMethod}
                        </div>
                      )}
                      {(action.documentationFields && action.documentationFields.length > 0) && (
                        <div className="action-detail-row">
                          <strong>Documentation:</strong>{' '}
                          {action.documentationFields.map(f => f.fieldType || f.documentationFieldTypeId || f.name).filter(Boolean).join(', ') || '—'}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}

              {/* Step Notes */}
              <div className="step-notes-input">
                <label>{t('stepNotesOptional')}</label>
                <textarea
                  value={stepNotes}
                  onChange={(e) => setStepNotes(e.target.value)}
                  placeholder={t('addNotesPlaceholder')}
                  rows={3}
                />
              </div>

              {/* Phase 3: Evidence Capture Button */}
              <div className="evidence-section">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowEvidenceCapture(true)}
                >
                  <Camera size={16} /> Capture Evidence
                </button>
                {capturedEvidence.filter(e => 
                  e.step_id === currentStep.stepId || e.step_id === currentStep.stepId
                ).length > 0 && (
                  <span className="evidence-count">
                    {capturedEvidence.filter(e => 
                      e.step_id === currentStep.stepId || e.step_id === currentStep.stepId
                    ).length} evidence captured
                  </span>
                )}
              </div>

              {/* Step Actions */}
              <div className="step-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowSkipDialog(true)}
                  disabled={isPaused}
                >
                  <SkipForward size={16} /> {t('skipStep')}
                </button>
                <button
                  className="btn btn-success"
                  onClick={handleCompleteStep}
                  disabled={loading || isPaused}
                >
                  <CheckCircle size={16} /> {t('completeStep')}
                </button>
              </div>
                </>
              )}
            </div>
          )}

          {/* Complete Protocol Button */}
          {currentProtocol.steps.every(s => s.isComplete || s.isSkipped) &&
           currentProtocol.status !== 'completed' && currentProtocol.status !== 'completed_with_skips' && (
            <div className="protocol-complete-section">
              <p>{t('allStepsFinished')}</p>
              <button className="btn btn-primary" onClick={handleCompleteProtocol} disabled={loading || isPaused}>
                <CheckCircle size={16} /> {t('completeProtocol')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Complete Task Button */}
      {allProtocolsComplete && (
        <div className="task-complete-section">
          <div className="complete-message">
            <CheckCircle size={32} color="#10b981" />
            <h3>{t('allProtocolsCompleted')}</h3>
            <p>
              {taskData.protocols?.some(p => p.status === 'completed_with_skips' || p.completedWithSkips) ? (
                t('someStepsSkippedReady')
              ) : (
                t('readyToCompleteTask')
              )}
            </p>
          </div>
          <button className="btn btn-success" onClick={handleCompleteTask} disabled={loading}>
            {t('completeTask')}
          </button>
        </div>
      )}

      {/* Phase 3: Evidence Capture Modal */}
      <EvidenceCapture
        isOpen={showEvidenceCapture}
        onClose={() => setShowEvidenceCapture(false)}
        taskId={taskData.id}
        stepId={currentStep?.stepId || ''}
        actionId={null}
        executionId={executionId}
        executorId={userId}
        executorName={executorInfo.name}
        onEvidenceCaptured={handleEvidenceCaptured}
      />

      {/* Phase 3: Deviation Alert Modal */}
      <DeviationAlert
        isOpen={showDeviationAlert}
        onClose={() => setShowDeviationAlert(false)}
        deviation={currentDeviation}
        onContinue={handleContinueAfterDeviation}
        onPause={() => setShowDeviationAlert(false)}
      />

      {/* Phase 3: Compliance Score Display Modal */}
      <ComplianceScoreDisplay
        isOpen={showComplianceScore}
        onClose={handleCloseComplianceScore}
        complianceData={complianceData}
        taskData={taskData}
      />

      {/* Skip Dialog - Using Modal component */}
      <Modal
        isOpen={showSkipDialog}
        onClose={() => setShowSkipDialog(false)}
        title={t('skipStepTitle')}
        size="medium"
      >
        <div className="skip-step-modal">
          <p>{t('pleaseProvideSkipReason')}:</p>
          <textarea
            value={skipReason}
            onChange={(e) => setSkipReason(e.target.value)}
            placeholder={t('skipReasonPlaceholder')}
            rows={4}
            autoFocus
            className="skip-reason-input"
          />
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setShowSkipDialog(false)}>
              {t('common:cancel')}
            </button>
            <button className="btn btn-primary" onClick={handleSkipStep} disabled={!skipReason.trim()}>
              {t('tasks:skipStep')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Error Modal */}
      <Modal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: '' })}
        title={t('common:error')}
        size="small"
      >
        <div className="error-message-content">
          <p>{errorModal.message}</p>
          <button className="btn btn-primary" onClick={() => setErrorModal({ isOpen: false, message: '' })}>
            {t('common:close')}
          </button>
        </div>
      </Modal>

      {/* Success Modal */}
      <Modal
        isOpen={successModal.isOpen}
        onClose={() => {
          setSuccessModal({ isOpen: false, message: '' });
          onBack();
        }}
        title={t('common:success')}
        size="small"
      >
        <div className="success-message-content">
          <p>{successModal.message}</p>
          <button 
            className="btn btn-primary" 
            onClick={() => {
              setSuccessModal({ isOpen: false, message: '' });
              onBack();
            }}
          >
            {t('common:ok')}
          </button>
        </div>
      </Modal>

      {/* Confirm Modal */}
      <Modal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, message: '', onConfirm: null })}
        title={t('common:confirm')}
        size="small"
      >
        <div className="confirm-message-content">
          <p>{confirmModal.message}</p>
          <div className="modal-actions">
            <button 
              className="btn btn-secondary" 
              onClick={() => setConfirmModal({ isOpen: false, message: '', onConfirm: null })}
            >
              {t('common:cancel')}
            </button>
            <button 
              className="btn btn-primary" 
              onClick={() => confirmModal.onConfirm && confirmModal.onConfirm()}
            >
              {t('common:confirm')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TaskExecutionView;

